/**
 * La shortlist, en pile d'onglets — reconstruction du sélecteur de Safari iOS 7.
 *
 * Chaque tweet est une « page » : une barre de chrome en haut (croix de
 * fermeture, titre, pastille de score), le contenu dessous, et l'onglet suivant
 * qui vient le recouvrir. On ne voit d'une carte que son chrome et le début de
 * sa page — assez pour la reconnaître, pas assez pour la lire, donc on tape.
 *
 * **Le modèle spatial : un trajet entre deux états** — la troisième
 * formulation de Vincent, qui remplace le cercle. Chaque carte a une pose
 * d'ENTRÉE (l'état « bas » : position de lecture, presque de face, à la
 * vitre) et une pose de SORTIE (l'état « haut » : loin, penchée, éteinte), et
 * le défilement la fait voyager de l'une à l'autre. Une carte sous la lecture
 * glisse depuis le bord bas dans sa pose d'entrée ; passé la sortie, elle
 * fond. Le cercle d'avant était un cas particulier de ce modèle (les états
 * dérivés d'un rayon) ; ici les deux états sont éditables directement aux
 * curseurs du banc, ce qui rend chaque problème de cadrage — le crop du haut
 * de carte, par exemple — réglable à l'œil plutôt qu'en se battant avec une
 * géométrie qui les impose.
 *
 * La descente n'est pas linéaire : `curve` fait décélérer les cartes vers la
 * sortie (elles se resserrent en haut, comme sur la roue), tandis que
 * l'inclinaison et la profondeur interpolent linéairement entre les états.
 * La scène reste épinglée nativement (`sticky`), la caméra fixe, et le
 * défilement garde son échelle : un pixel de doigt = un pixel de trajet.
 *
 * Deux entorses délibérées à la pureté de la scène :
 *   - l'ordre de peinture est déclaré (`z-index` croissant), pas déduit de la
 *     profondeur : le conteneur est en `transform-style: flat` parce qu'en
 *     `preserve-3d` le navigateur écrase le z-index (§D37) ;
 *   - l'ouverture d'une carte quitte le tapis : elle vient à plat au premier
 *     plan, les autres s'écartent dans la direction où elles étaient déjà, et
 *     la fermeture rejoue le même trajet à l'envers — rien ne se téléporte.
 */

import { motion, useDragControls } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

import {
  BASE_CARD_W,
  CHROME_HEIGHT,
  MOTION,
  OPENING,
  SLOW_SPRING,
  STACK_SPRING,
  WHEEL,
  WHEEL_FOLLOW,
  budgetedCardWidth,
  collapsedStackHeight,
  createScrollTracker,
  maxPop,
  openDuration,
  openPoseTransition,
  openRotateTransition,
  poseAt,
  spreadTransition,
  stackContext,
  type SceneMeasure,
} from "./shortlist-geometry.js";

/**
 * La géométrie vit désormais dans `shortlist-geometry.ts` — le rendu WebGL
 * (§D42, §D43) doit poser les cartes au même endroit que le DOM, et la seule
 * garantie solide est qu'ils appellent le même code. On la ré-exporte ici :
 * le banc d'essai importe `WHEEL`/`OPENING`/`MOTION` depuis ce fichier depuis
 * toujours, et ses curseurs doivent continuer de muter LES MÊMES objets que
 * lisent les deux moteurs.
 */
export {
  BASE_CARD_W,
  CHROME_HEIGHT,
  MAX_STACK_W,
  MOTION,
  OPENING,
  STACK_SPRING,
  WHEEL,
} from "./shortlist-geometry.js";

/**
 * Sur-échantillonnage du raster des cartes — NEUTRALISÉ (1). Il datait de
 * l'époque où le pop de lecture partait au GPU en translateZ ; depuis que
 * les profondeurs positives sont cuites en échelle 2D, le raster est déjà
 * peint à la taille finale, et le ×1,3 ne faisait plus que multiplier par
 * 1,7 la mémoire de texture de cartes plein écran — pur coût, aggravant la
 * perf téléphone. Le mécanisme reste en place si un cas de flou résiduel
 * réapparaît.
 */
const RASTER_SS = 1;

export interface StackItemProps {
  index: number;
  /** Nombre total de cartes — les piles courtes s'ancrent à la position de lecture. */
  count: number;
  /** Axe caméra : position du centre du viewport dans le repère de la pile. */
  focus: number;
  /** Largeur mesurée de la pile — donne la taille des cartes et leur échelle. */
  width: number;
  /** Vitesse de défilement lissée (px/s) — nourrit `speedDepth`. */
  speed: number;
  /** Position viewport du bord haut de la scène — sert au centrage d'ouverture. */
  stageTop: number;
  /** Index de la carte ouverte, ou `null` si la pile est au repos. */
  openIndex: number | null;
  /** Hauteur de la carte ouverte. */
  expandedHeight: number;
  onSelect: () => void;
  /**
   * Fermer la carte ouverte — le tirer-vers-le-bas s'en sert, et lui passe
   * le décalage atteint : le rendu GL reprend le retour à cette pose-là, au
   * lieu de repartir du point d'arrivée du voyage.
   */
  onClose?: (offsetY?: number) => void;
  children: ReactNode;
}

export function StackItem({
  index,
  count,
  focus,
  width,
  speed,
  stageTop,
  openIndex,
  expandedHeight,
  onSelect,
  onClose,
  children,
}: StackItemProps) {
  const isOpen = openIndex === index;
  const collapsed = openIndex === null;

  /**
   * La fermeture doit être animée, et le suivi du scroll instantané — deux
   * régimes sur le MÊME état « au repos ». On distingue le rendu où la pile
   * vient de se refermer (l'index ouvert vient de passer à null) : celui-là
   * voyage au ressort, les suivants suivent le doigt sans ressort. Aucun
   * rendu spontané n'a lieu pendant le retour, le ressort va au bout.
   */
  const prevOpen = useRef(openIndex);
  const justClosed = collapsed && prevOpen.current !== null;
  useEffect(() => {
    prevOpen.current = openIndex;
  });

  /**
   * Pendant la TRACTION, la carte reste à sa place dans l'empilement : la
   * suivante continue de la recouvrir, et c'est le glissement qui la révèle
   * progressivement de dessous — le geste d'extraire une carte d'un paquet.
   * Elle ne passe au premier plan qu'une fois extraite (fin de la phase de
   * traction), pour le voyage vers le centre. La passer devant dès le clic
   * la « dé-croppait » d'un coup : un pop, pas une extraction.
   */
  const [lifted, setLifted] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setLifted(false);
      return;
    }
    const id = setTimeout(() => setLifted(true), openDuration() * OPENING.pullAt * 1000);
    return () => clearTimeout(id);
  }, [isOpen]);

  /**
   * Tirer la carte ouverte vers le bas la referme — le geste des sheets iOS.
   * Le drag ne démarre que depuis le chrome et les zones mortes : jamais
   * depuis un bouton, un lien, un champ, ni la zone de contenu défilante
   * (`data-noclosedrag`), sinon il volerait leur geste.
   */
  const dragControls = useDragControls();

  /**
   * La pose vient du module de géométrie partagé — le même que lit le moteur
   * GL. Le contexte (butées de repos et de fin de liste, profondeur du milieu
   * à la vitesse courante) est recalculé par carte : c'est du O(1), et le
   * garder ici évite de faire remonter un état de frame dans `Stack`, qui
   * re-rendrait toute la pile à chaque pixel de défilement.
   */
  const ctx = stackContext({
    count,
    focus,
    width,
    speed,
    stageTop,
    H: window.innerHeight,
    W: window.innerWidth,
  });
  const { cardH, scale } = ctx;
  const { yCenter, y, z3d, tilt, kPop, fade, visible } = poseAt(ctx, index);

  /**
   * Le grossissement du CONTENU. Sur le trajet, c'est le pop de la pose ;
   * OUVERTE, la carte garde l'échelle de LECTURE — `maxPop()`, et non 1
   * (§D45). Elle a donc exactement la largeur et le corps de typo de la carte
   * qu'on vient de taper, au lieu de retomber à la largeur de base et de
   * donner ce « dézoom » au tap.
   */
  const kContent = isOpen ? maxPop() : kPop;
  /**
   * La boîte de contenu, en px de CONCEPTION : ce que le wrapper met en page
   * avant sa mise à l'échelle. Divisée par `kContent`, elle rend une boîte
   * visible de `expandedHeight` px quel que soit le grossissement.
   */
  const contentH = (isOpen ? expandedHeight / kContent : cardH) / scale;

  // `initial={false}` rend le franchissement du culling invisible.
  if (collapsed && !visible) return null;

  const target = collapsed
    ? { y, z: z3d, rotateX: tilt, opacity: fade }
    : isOpen
      ? /**
         * Ouverte : d'abord TIRÉE, puis centrée. Trois keyframes — la pose
         * courante (null), la pose tirée, le centre de l'écran. La traction
         * se fait dans l'AXE DE LA CARTE (`WHEEL.pull` px le long de son
         * plan, l'angle tenu) : c'est le geste de sortir une carte d'une
         * pile, pas de la soulever. Le centre est exprimé en viewport puis
         * reconverti via `stageTop` (stable : le scroll est verrouillé
         * pendant l'ouverture).
         */
        {
          y: [
            null,
            yCenter - cardH / 2 - stageTop - OPENING.pull * Math.cos((tilt * Math.PI) / 180),
            (window.innerHeight - expandedHeight) / 2 - stageTop,
          ] as [null, number, number],
          z: [null, z3d - OPENING.pull * Math.sin((tilt * Math.PI) / 180), 0] as [
            null,
            number,
            number,
          ],
          rotateX: [null, tilt, 0] as [null, number, number],
          opacity: 1,
        }
      : /**
         * Écartée : poussée FRANCHE depuis sa propre pose — vers le haut pour
         * celles au-dessus de la piochée, vers le bas pour celles en dessous.
         * Ni récession ni changement d'angle : l'ancienne cible reculait les
         * cartes en Z, et la projection les tirait vers l'axe caméra — ce qui
         * annulait la poussée verticale à l'écran, elles semblaient fondre
         * sur place au lieu d'être chassées.
         */
        {
          y:
            yCenter -
            cardH / 2 -
            stageTop +
            (index < openIndex ? -1 : 1) *
              (OPENING.spreadDist + Math.abs(index - openIndex) * 30),
          z: z3d,
          rotateX: tilt,
          opacity: 0,
        };

  return (
    <motion.div
      // Origine par défaut (50 % 50 %) : la carte pivote autour de son centre,
      // qui est aussi son point d'attache au trajet.
      className="absolute top-0"
      style={{
        // Colonne centrée à la largeur (plafonnée) transmise par Stack.
        width,
        left: `calc(50% - ${width / 2}px)`,
        /**
         * Ordre de peinture croissant : plus l'index est grand, plus la carte
         * est bas sur l'arc, donc proche — elle recouvre celles du fond.
         * C'est aussi ce qui coupe chaque page au ras de sa bande visible.
         */
        zIndex: isOpen && lifted ? 999 : index + 1,
        pointerEvents: !collapsed && !isOpen ? "none" : "auto",
      }}
      /**
       * `initial={false}` : au montage, la carte se peint directement dans sa
       * pose — sans ça, chaque chargement dépliait la pile en ressort depuis
       * une pose par défaut. Les ressorts ne jouent qu'aux changements
       * suivants : ouverture, fermeture.
       */
      initial={false}
      animate={{ ...target, height: isOpen ? expandedHeight : cardH }}
      // Quatre régimes : la roue suit le doigt rigidement ; l'OUVERTURE joue
      // ses keyframes (traction dans l'axe de la carte, puis centrage) ; la
      // fermeture et les cartes écartées voyagent au ressort. Le slow-mo
      // ralentit tout ×6.
      transition={
        collapsed && !justClosed
          ? WHEEL_FOLLOW
          : isOpen
            ? {
                y: openPoseTransition(),
                z: openPoseTransition(),
                rotateX: openRotateTransition(),
                height: MOTION.slow ? SLOW_SPRING : STACK_SPRING,
                opacity: { duration: 0.2 },
              }
            : // Cartes écartées pendant une ouverture : tempo par côté.
              spreadTransition(openIndex !== null && index > openIndex)
      }
      onClick={collapsed ? onSelect : undefined}
      drag={isOpen ? "y" : false}
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0, bottom: 0.6 }}
      onPointerDown={(event) => {
        if (!isOpen) return;
        const target = event.target as HTMLElement;
        if (target.closest("button, a, textarea, input, [data-noclosedrag]")) return;
        dragControls.start(event);
      }}
      onDragEnd={(_, info) => {
        if (isOpen && (info.offset.y > 120 || info.velocity.y > 500)) onClose?.(info.offset.y);
      }}
    >
      {/**
        * Le contenu est mis en page à la largeur de conception puis mis à
        * l'échelle de la carte — et SUR-ÉCHANTILLONNÉ (`RASTER_SS`) : le
        * niveau intérieur agrandit le raster en 2D, l'extérieur redescend
        * d'autant, si bien que la texture est peinte plus fine que sa taille
        * d'affichage. Sans ça, Safari rastérise à la taille de mise en page
        * puis laisse le GPU agrandir — par l'échelle du wrapper ET par le
        * grossissement caméra de la profondeur positive de lecture — et le
        * texte des cartes est flou sur téléphone.
        */}
      <div
        style={{
          width: BASE_CARD_W * RASTER_SS,
          height: contentH * RASTER_SS,
          /**
           * La translation recentre la croissance du pop : l'échelle part de
           * l'origine gauche (le wrapper est plus large que la boîte, une
           * origine « center » décalait tout le contenu), et on rend la
           * moitié du surplus à gauche pour que la carte grossisse
           * symétriquement. Elle recentre aussi bien l'état ouvert, dont le
           * grossissement n'est plus 1 mais `maxPop()`.
           */
          transform: `translateX(${(width * (1 - kContent)) / 2}px) scale(${
            (scale * kContent) / RASTER_SS
          })`,
          transformOrigin: "top left",
        }}
      >
        <div
          style={{
            width: BASE_CARD_W,
            height: contentH,
            transform: `scale(${RASTER_SS})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * La barre de chrome d'un onglet.
 *
 * Trois zones, comme dans un navigateur : une action à gauche, un titre au
 * centre, un état à droite. Le titre est centré **sur la carte** et non entre
 * les deux boutons — les slots latéraux sont donc en `absolute`, sinon un titre
 * long décalerait le centre optique d'une carte à l'autre et la pile
 * ondulerait.
 *
 * `variant` dit ce que fait le bouton de gauche : fermer l'onglet quand la pile
 * est au repos, revenir à la pile quand il est ouvert. Même position, même
 * taille — c'est toujours « le bouton qui enlève ce que je regarde ».
 */
export function TabChrome({
  onAction,
  right,
  children,
}: {
  /** Revenir à la pile — présent seulement sur la carte ouverte. La vue
   *  liste, elle, n'a AUCUN bouton : toute la carte est la cible d'ouverture,
   *  et retirer un tweet de la shortlist n'est pas un geste de survol. */
  onAction?: () => void;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className="relative flex shrink-0 items-center justify-center"
      style={{
        height: CHROME_HEIGHT,
        // Plus clair que la page : le chrome est la partie de l'objet qui
        // accroche la lumière, c'est ce qui le détache du contenu. Dosage
        // volontairement faible — répété huit fois dans une pile, un contraste
        // plus franc raye l'écran de bandes et masque la colonne des titres,
        // qui est justement ce qu'on vient y lire.
        background: "linear-gradient(180deg, rgba(255,255,255,0.058), rgba(255,255,255,0.018))",
        borderBottom: "1px solid rgba(255,255,255,0.055)",
      }}
    >
      {onAction && (
        <button
          onClick={(event) => {
            event.stopPropagation();
            onAction();
          }}
          aria-label="Retour à la pile"
          className="absolute left-0 top-0 flex h-full w-11 items-center justify-center text-[var(--color-muted)] transition hover:text-[var(--color-text)] active:scale-90"
        >
          <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
            <path
              d="M14.5 5L8 12l6.5 7"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      <div className="flex min-w-0 max-w-[62%] items-center gap-1.5 text-[13px] font-medium">
        {children}
      </div>

      {right && (
        <div className="absolute right-2 top-0 flex h-full items-center gap-1.5">{right}</div>
      )}
    </div>
  );
}

/**
 * Où tombe le centre du viewport dans le repère de la pile — l'axe de la
 * caméra. Il suit le défilement : la mesure passe par un
 * `requestAnimationFrame` (l'événement part bien plus souvent qu'on ne
 * repeint) et l'écoute est en capture sur `window`, parce que les événements
 * de défilement ne remontent pas et qu'on ne veut pas coder ici l'identité de
 * l'élément qui défile.
 *
 * `null` tant que rien n'a été mesuré — et rien ne se peint tant que c'est
 * `null`. La mesure se fait en `useLayoutEffect`, après le montage mais AVANT
 * la peinture : la première image montre la scène déjà posée. Partir d'une
 * valeur arbitraire faisait converger la pile en ressort sous les yeux à
 * chaque chargement.
 */
function useViewportFocus(
  ref: React.RefObject<HTMLDivElement | null>,
  stageRef: React.RefObject<HTMLDivElement | null>,
): SceneMeasure | null {
  const [state, setState] = useState<SceneMeasure | null>(null);

  useLayoutEffect(() => {
    // Le tracker est partagé avec le moteur GL : le lissage de la vitesse
    // nourrit `speedDepth`, un effet visible, et deux lissages légèrement
    // différents se verraient au A/B des pichenettes.
    const tracker = createScrollTracker(() => {
      const el = ref.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      return {
        focus: window.innerHeight / 2 - rect.top,
        width: rect.width,
        stageTop: stageRef.current ? stageRef.current.getBoundingClientRect().top : 0,
      };
    }, setState);

    tracker.start();
    return () => tracker.stop();
  }, [ref, stageRef]);

  return state;
}

/**
 * Le conteneur de la pile. Il porte la perspective : c'est lui la caméra, et
 * il n'y en a qu'une pour toute la scène — sur les cartes elles-mêmes, chacune
 * aurait son propre point de fuite.
 */
export function Stack({
  count,
  openIndex,
  expandedHeight,
  children,
}: {
  count: number;
  openIndex: number | null;
  expandedHeight: number;
  /** Reçoit l'axe caméra, la largeur, la vitesse et la position de la scène. */
  children: (focus: number, width: number, speed: number, stageTop: number) => ReactNode;
}) {
  /**
   * Hauteur de défilement : un écran pour le quart visible, plus l'arc que
   * les cartes excédentaires devront parcourir pour atteindre la position de
   * lecture. La roue étant épinglée au viewport, cette hauteur ne « contient »
   * rien — elle ne fait que dimensionner la course du scroll. L'excès de
   * course est absorbé par la borne sur l'avancement, qui arrête la pile net
   * sur la dernière carte — une butée de fin de liste, pas un défaut.
   */
  const H = window.innerHeight;
  const collapsedHeight = collapsedStackHeight(count, H, window.innerWidth);
  const ref = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const measured = useViewportFocus(ref, stageRef);

  return (
    <motion.div
      ref={ref}
      className="relative"
      /**
       * La hauteur du conteneur ne dépend PAS de l'état ouvert/fermé — c'est
       * ce qui préserve la position de défilement. Quand il rétrécissait à
       * l'ouverture, le navigateur écrêtait scrollTop à la nouvelle course,
       * et la fermeture repartait du début de la liste. Constant, rien n'est
       * écrêté : la scène épinglée montre la carte ouverte où qu'on soit, et
       * on referme exactement là où on était. Le max loge la carte ouverte
       * sur les piles plus courtes qu'elle.
       */
      initial={false}
      animate={{ height: collapsedHeight }}
      transition={MOTION.slow ? SLOW_SPRING : STACK_SPRING}
    >
      {/**
        * LA SCÈNE EST ÉPINGLÉE NATIVEMENT (`sticky`), et c'est le correctif
        * définitif du désaxage au défilement. L'ancien montage compensait le
        * scroll dans les poses (+W) : le scroll natif déplaçait le conteneur
        * dans la frame, la compensation arrivait à la frame suivante, et
        * pendant ce battement toute la scène glissait AVANT projection — un
        * glissement uniforme que la projection redistribue inégalement selon
        * la profondeur, d'où un balancement hors axe. En `sticky`, c'est le
        * compositeur qui tient la scène, dans la même frame que le scroll.
        * Le conteneur au-dessus ne sert plus qu'à donner sa course au
        * défilement ; seul l'angle de roue θ dépend encore du scroll.
        *
        * La scène porte la caméra : `perspective` (grand angle — voir WHEEL)
        * et une origine FIXE dans son repère, sans aucune compensation.
        * `flat` et non `preserve-3d` : l'ordre de peinture reste déclaré
        * (§D37).
        */}
      <div
        ref={stageRef}
        className="sticky top-0"
        style={{
          /**
           * Au plus la hauteur du conteneur : une scène qui déborde rend sa
           * course au défilement (c'était le « léger scroll » qui rognait la
           * carte ouverte), et un sticky sans dépassement de conteneur ne
           * s'épingle jamais. Le conteneur étant constant, la scène l'est
           * aussi — la carte ouverte y tient par construction.
           */
          height: Math.min(window.innerHeight, collapsedHeight),
          perspective: `${WHEEL.perspective}px`,
          // L'axe caméra aussi est un objectif viewport, compensé comme les
        // poses — sinon l'origine glisserait avec le rail pendant que les
        // cartes restent collées, et le cisaillement reviendrait.
        perspectiveOrigin: `50% ${WHEEL.originY - (measured?.stageTop ?? 0)}px`,
          transformStyle: "flat",
          /**
           * `clip`, pour deux raisons. Les cartes-écrans débordent de la
           * scène (c'est leur nature) ; sans clip, ce débordement de boîtes
           * absolues compte dans la zone défilable de `main` et ajoute une
           * course parasite — la roue sur-tournait à vide, et sur les petites
           * piles la sticky, à course nulle, glissait avec le flux. Et
           * visuellement, la scène est un cadre : ce qui en sort n'existe pas.
           */
          overflow: "clip",
        }}
      >
        {/* Première peinture seulement une fois la caméra placée : les cartes
            montent alors directement dans leur pose finale, sans animation. */}
        {measured === null
          ? null
          : children(
              measured.focus,
              /**
               * La largeur transmise BUDGÈTE le grossissement caméra : la
               * carte la plus poppée (profondeur positive maximale du
               * trajet) doit atteindre exactement la largeur disponible —
               * jamais plus. Sans ce budget, la carte de lecture débordait
               * de l'écran d'un téléphone (×1,16 sur une carte déjà pleine
               * largeur), tranchée des deux bords.
               */
              budgetedCardWidth(measured.width),
              measured.speed,
              measured.stageTop,
            )}
      </div>
    </motion.div>
  );
}
